// v3 guided flow shell: top bar, progress ribbon, the current step's page, and previous / next on
// every page. All flow decisions (order, status, locking, what blocks Next) come from the pure
// step model; this component only renders them and calls store actions.
//
// Component tree
//   GuidedApp
//   ├─ top bar (brand, account chip, sync status, export / import / benchmarks / clear)
//   ├─ ProgressRibbon                      steps + status + jump
//   ├─ <main> current step page
//   │    title        TitleCard
//   │    account      AccountStep
//   │    selection    UseCaseSelectionStep
//   │    deal         DealEconomicsStep
//   │    use-case     UseCaseStep          one page per selected use case
//   │    review       ReviewStep           every input, editable inline
//   │    roi          RoiSummaryStep       program value x attribution = Clay-attributed value
//   └─ footer nav (Previous, "Step n of N", [Excel export], Next; on the ROI summary the primary button opens the executive deck, behind the review gate)
//
// Layout: the shell is a full-height flex column (bar, ribbon, scrolling <main>, footer nav), so the
// header, ribbon and footer stay put and <main> is the only scroller.

import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { readStoredJson } from '../../../_shared/read-stored-json';
import { SyncStatus } from '../calculator/SyncStatus';
import { iconUrl } from '../../data/icons';
import {
  nextBlockReason,
  stepNeighbors,
  type Step,
} from '../../flow/step-model';
import {
  calculatorActions,
  stepModelInputOf,
  stepsOf,
  useCalculatorStore,
} from '../../state/calculator-store';
import { forceSync, useAutosave } from '../../state/autosave-runtime';
import { useSyncStore } from '../../state/autosave-store';
import { showNotice, useNoticeStore, type Notice } from '../../state/notice-store';
import {
  SESSION_KEY,
  applyLoaded,
  loadFromSession,
  loadFromShare,
  serializeSession,
} from '../../state/load-paths';
import { Ph } from '../Icon';
import { AccountStep } from './AccountStep';
import { DealEconomicsStep } from './DealEconomicsStep';
import { ProgressRibbon } from './ProgressRibbon';
import { ReviewStep } from './ReviewStep';
import { RoiSummaryStep } from './RoiSummaryStep';
import { SessionTools } from './SessionTools';
import { TitleCard } from './TitleCard';
import { UseCaseStep } from './UseCasePage';
import { UseCaseSelectionStep } from './UseCaseSelectionStep';
import { ReviewGateDialog } from './deck/ReviewGateDialog';
import { useOpenDeck } from './useOpenDeck';
import { downloadWorkbook } from './workbook-download';
import './v3.css';

interface GuidedAppProps {
  /** The `?s=` share link value. Read once through the migration (v3 and v2 links both open here). */
  initialShare?: string | null;
  /** Called once the share link has been read, so the route can strip it from the URL. */
  onShareConsumed?: () => void;
}

/** Hydrate from the share link, or else from the session parked by a hard reload. Runs once on mount. */
function loadInitial(initialShare: string | null | undefined): Notice | null {
  const state = useCalculatorStore.getState();
  if (initialShare) {
    const loaded = loadFromShare(initialShare, state.account);
    if (!loaded) return { tone: 'error', text: 'That share link could not be opened.' };
    applyLoaded(loaded);
    return {
      tone: 'info',
      text: loaded.recheck.length
        ? `Share link opened. ${loaded.recheck.length} ${loaded.recheck.length === 1 ? 'answer needs' : 'answers need'} a re-check; they are flagged in the review. Choose an account or demo mode to continue.`
        : 'Share link opened. Choose an account or demo mode to continue.',
    };
  }
  // Only a fresh page restores a parked session; the module-scope store already holds live state
  // when the shell remounts after the deck.
  if (state.path !== null || state.epoch !== 0) return null;
  // Unavailable or unreadable storage reads as nothing parked: start fresh. Validation is the
  // migration's job (loadFromSession); this only decodes JSON.
  const parked = readStoredJson(sessionStorage, SESSION_KEY, z.unknown(), null);
  const loaded = parked === null ? null : loadFromSession(parked);
  if (loaded) applyLoaded(loaded);
  return null;
}

function StepBody({ step }: { step: Step }) {
  switch (step.kind) {
    case 'title':
      return <TitleCard onBuildBusinessCase={calculatorActions.startBusinessCase} />;
    case 'account':
      return <AccountStep />;
    case 'selection':
      return <UseCaseSelectionStep />;
    case 'deal':
      return <DealEconomicsStep />;
    case 'use-case':
      return step.useCaseId ? <UseCaseStep key={step.useCaseId} useCaseId={step.useCaseId} /> : null;
    case 'review':
      return <ReviewStep />;
    case 'roi':
      return <RoiSummaryStep />;
  }
}

function nextLabel(step: Step, next: Step | null): string {
  // Story 43: the last use case page hands off to the review list.
  if (step.kind === 'use-case' && next?.kind === 'review') return 'View summary';
  // The selection step hands off to sizing: the blank workbook is the alternative to filling in now.
  if (step.kind === 'selection') return 'Fill use case inputs now';
  if (next?.kind === 'review') return 'Review inputs';
  if (next?.kind === 'roi') return 'View ROI summary';
  if (step.kind === 'roi') return 'View the executive deck';
  return 'Next';
}

export function GuidedApp({ initialShare, onShareConsumed }: GuidedAppProps) {
  // The whole state: the shell re-renders on any change, which is cheap and keeps it simple.
  const state = useCalculatorStore();
  const sync = useSyncStore();
  const notice = useNoticeStore((s) => s.notice);
  useAutosave();
  const deck = useOpenDeck();
  const mainRef = useRef<HTMLElement>(null);
  const firstStep = useRef(true);

  // The route's callback changes identity when the URL does; the effect below must not re-run for that.
  const onShareConsumedRef = useRef(onShareConsumed);
  useEffect(() => {
    onShareConsumedRef.current = onShareConsumed;
  });
  useEffect(() => {
    // Every load path goes through the migration (state/load-paths.ts); a load is never an edit.
    // The input is fixed for the life of the route, so this runs once (twice under StrictMode,
    // which is harmless: loading the same link again is idempotent, and a session restores only
    // into a pristine store).
    showNotice(loadInitial(initialShare));
    if (initialShare) onShareConsumedRef.current?.();
  }, [initialShare]);

  // Park the sizing for a hard reload (the store lives in module scope, so SPA navigation to the
  // deck keeps it; a reload or tab restore does not).
  useEffect(() => {
    const park = () => {
      try {
        const payload = serializeSession(useCalculatorStore.getState());
        if (payload) sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
      } catch {
        /* quota or serialization failure: losing the parked session is acceptable */
      }
    };
    window.addEventListener('pagehide', park);
    return () => {
      window.removeEventListener('pagehide', park);
      park();
    };
  }, []);

  const steps = stepsOf(state);
  const step = steps.find((s) => s.id === state.currentStepId) ?? steps[0];
  const { previous, next } = stepNeighbors(steps, step.id);

  // New page: scroll back to the top of the step.
  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false;
      return;
    }
    mainRef.current?.scrollTo?.({ top: 0 });
  }, [step.id]);

  const onTitle = step.kind === 'title';
  const blockReason =
    nextBlockReason(stepModelInputOf(state), step.id) ??
    (next?.locked ? 'Choose an account or demo mode to continue' : null);
  const canGoBack = previous !== null || state.path === 'business-case';
  const index = steps.findIndex((s) => s.id === step.id);

  const onJump = (id: string) => {
    const target = steps.find((s) => s.id === id);
    if (!target || target.locked || id === step.id) return;
    calculatorActions.goToStep(id);
  };

  return (
    <div className="vc3">
      <header className="vc3-bar">
        <button
          type="button"
          className="vc3-brand"
          aria-label="Value calculator home"
          title="Back to the start. Your case is kept."
          onClick={calculatorActions.showTitle}
        >
          <img src={iconUrl('assets/logo/Clay_Logo_Primary_Blk.png')} alt="" />
          <span>Value calculator</span>
        </button>
        <div className="vc3-bar-right">
          {state.account?.kind === 'demo' && <span className="vc3-chip vc3-chip-demo">Demo mode · not saved</span>}
          {state.account?.kind === 'account' && (
            <span className="vc3-chip">
              {state.account.accountName}
              {state.account.opportunityName ? ` · ${state.account.opportunityName}` : ''}
            </span>
          )}
          {state.account?.kind === 'account' && (
            <SyncStatus
              status={sync.status}
              lastSyncedAt={sync.lastSyncedAt}
              oauthUrl={sync.oauthUrl}
              errorMessage={sync.errorMessage}
              onForceSync={forceSync}
            />
          )}
          {state.path === 'business-case' && <SessionTools />}
        </div>
      </header>

      {notice && (
        <div className={`vc3-notice${notice.tone === 'error' ? ' vc3-notice-error' : ''}`} role={notice.tone === 'error' ? 'alert' : 'status'}>
          <span style={{ flex: 1 }}>{notice.text}</span>
          <button type="button" className="vc3-linkbtn" onClick={() => showNotice(null)}>
            Dismiss
          </button>
        </div>
      )}

      {state.path !== null && (
        <ProgressRibbon steps={steps} currentStepId={step.id} onJump={onJump} />
      )}

      <main className="vc3-main" ref={mainRef}>
        <StepBody step={step} />
      </main>

      {!onTitle && (
        <nav className="vc3-nav" aria-label="Step navigation">
          <button
            type="button"
            className="vc3-btn vc3-btn-ghost"
            disabled={!canGoBack}
            onClick={calculatorActions.previous}
          >
            <Ph name="arrow-left" size={14} /> Previous
          </button>
          <div className="vc3-nav-mid">
            <span>
              Step {index + 1} of {steps.length}
            </span>
            {blockReason && (
              <span className="vc3-nav-block" role="status">
                {blockReason}
              </span>
            )}
            {step.kind === 'roi' && deck.failed && (
              <span className="vc3-nav-block" role="alert">
                The deck couldn’t be prepared because browser storage is unavailable.
              </span>
            )}
          </div>
          <div className="vc3-nav-right">
            {/* Excel-green export buttons: the blank template at use case selection, the filled one at review. */}
            {step.kind === 'selection' && (
              <button
                type="button"
                className="vc3-btn vc3-btn-xlsx"
                disabled={state.selected.length === 0}
                title={state.selected.length === 0 ? 'Select a use case first' : 'A template with Clay defaults on every input, for the customer to fill in later'}
                onClick={() => downloadWorkbook({ blank: true })}
              >
                <Ph name="microsoft-excel-logo" size={16} /> Export blank .xlsx for customer
              </button>
            )}
            {step.kind === 'review' && (
              <button
                type="button"
                className="vc3-btn vc3-btn-xlsx"
                disabled={state.selected.length === 0}
                title={state.selected.length === 0 ? 'Select a use case first' : 'A live-formula workbook with your inputs, for the customer to review'}
                onClick={() => downloadWorkbook()}
              >
                <Ph name="microsoft-excel-logo" size={16} /> Export filled .xlsx for customer review
              </button>
            )}
            {step.kind === 'roi' ? (
              <button type="button" className="vc3-btn" disabled={state.selected.length === 0} onClick={deck.open}>
                {nextLabel(step, next)} <Ph name="arrow-right" size={14} />
              </button>
            ) : (
              <button
                type="button"
                className="vc3-btn"
                disabled={next === null || blockReason !== null}
                onClick={calculatorActions.next}
              >
                {nextLabel(step, next)} <Ph name="arrow-right" size={14} />
              </button>
            )}
          </div>
        </nav>
      )}
      <ReviewGateDialog open={deck.gateOpen} onAck={deck.acknowledge} />
    </div>
  );
}
